import {computed, ref, type ComputedRef, type Ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {extractError} from '@/ts/composables/pure/use-loading-data-ref';

/** One page as the backend hands it back, normalised across endpoints. */
export interface PagedResult<T> {
  items: T[];
  /** The page the backend actually served. The next index is derived from this, never from a counter. */
  page: number;
  /** How many rows matched before the page window — the number a result count reports. */
  totalElements: number;
}

/** What a caller must supply to {@link usePagedList}. */
export interface UsePagedListOptions<T> {
  /** Fetches one page. Receives the zero-based index the list wants next. */
  fetchPage(page: number): Promise<PagedResult<T>>;
  /** Row identity: dedupes appends and counts progress. */
  key(item: T): string;
  /**
   * Called on a failed page. `first` distinguishes the whole-view failure from a partial one.
   * The list already tracks the failure itself — this is for logging, or for a caller that would
   * rather raise a toast than render the inline state.
   */
  onError?(err: unknown, first: boolean): void;
}

/** The list state and controls {@link usePagedList} returns. */
export interface UsePagedList<T> {
  items: Ref<T[]>;
  totalElements: Ref<number>;
  /** Distinct rows held. Not items.length, which a repeated page would inflate. */
  loadedCount: ComputedRef<number>;
  allLoaded: ComputedRef<boolean>;
  /** The walk ended early: a page added nothing while the backend still reports more. */
  stoppedShort: ComputedRef<boolean>;
  loading: Ref<boolean>;
  loadingMore: Ref<boolean>;
  error: Ref<boolean>;
  moreError: Ref<boolean>;
  /**
   * The most recent failure, resolved through the same `extractError` the rest of the app uses, so
   * a caller can show the real reason instead of a generic line. Null while nothing has failed.
   */
  errorMessage: Ref<string | null>;
  loadFirst(): Promise<void>;
  /**
   * Re-fetches every page currently held and swaps them in one go, leaving `loading` alone and the
   * row count unchanged — so a caller can refresh stale rows without collapsing the list under a
   * reader who had scrolled into it. Does nothing if the list is empty or a fetch is already in
   * flight, and keeps what is on screen if any page fails.
   *
   * Resolves `true` only when the swap happened, so a caller gating a staleness window on it does
   * not buy quiet time off a refresh that failed.
   */
  refresh(): Promise<boolean>;
  /**
   * Fetches the next page. Resolves `false` when a guard stopped it (already loading, complete, or
   * sitting on a failure) — a caller driving this from an observer must not re-arm on a `false`,
   * or the re-observe fires the callback straight back and spins.
   */
  loadMore(): Promise<boolean>;
  /** Clears the failure and the stop latch, then retries. Same return contract as loadMore. */
  retryMore(): Promise<boolean>;
}

/**
 * The paging half of a "load a page at a time" list: page indices, dedupe, completion, and the two
 * failure modes. Knows nothing about how the rows arrive or how they are presented, so it serves
 * both an infinite-scroll grid and a "Load more" button.
 *
 * The guards here are all ones that cost real bugs:
 * - Only the newest request may write, or a slow response for a list the user has left appends its
 *   rows to the one now on screen.
 * - The next index comes from the response's own `page`, so a short or over-full page cannot make
 *   it drift into skipping a page or asking for one twice.
 * - Appends are deduped and completion counts distinct rows, so a repeated window cannot satisfy
 *   the total while the tail stays unreachable.
 * - A page that adds nothing latches instead of looping, and says so through `stoppedShort` rather
 *   than quietly rewriting the total down to what arrived.
 */
export function usePagedList<T>({fetchPage, key, onError}: UsePagedListOptions<T>): UsePagedList<T> {
  const items = ref<T[]>([]) as Ref<T[]>;
  const totalElements = ref<number>(0);
  const nextPage = ref<number>(0);
  const exhausted = ref<boolean>(false);
  const refreshing = ref<boolean>(false);
  const loading = ref<boolean>(true);
  const loadingMore = ref<boolean>(false);
  const error = ref<boolean>(false);
  const moreError = ref<boolean>(false);
  const errorMessage = ref<string | null>(null);
  const {t: $t} = useI18n();

  let requestId = 0;

  const loadedCount = computed<number>(() => new Set(items.value.map(key)).size);
  const allLoaded = computed<boolean>(() => exhausted.value || loadedCount.value >= totalElements.value);
  const stoppedShort = computed<boolean>(() => exhausted.value && loadedCount.value < totalElements.value);

  const loadPage = async(id: number): Promise<void> => {
    const result = await fetchPage(nextPage.value);
    if (id !== requestId) {
      return;
    }
    const known = new Set(items.value.map(key));
    const fresh = result.items.filter((item) => !known.has(key(item)));
    items.value = [...items.value, ...fresh];
    // A page that served nothing does not move the index: retryMore has to ask for THAT page
    // again, or its rows stay unreachable behind a total that still counts them. A page that did
    // serve rows moves on even if we already held them all — the server answered for it, so
    // re-asking would latch on the same page for ever. A response without `page` would also make
    // `page + 1` NaN, which reaches the backend as page=NaN and 400s.
    if (result.items.length > 0) {
      // eslint-disable-next-line require-atomic-updates -- only the request still owning requestId reaches here
      nextPage.value = Number.isFinite(result.page) ? result.page + 1 : nextPage.value + 1;
    }
    totalElements.value = result.totalElements;
    exhausted.value = fresh.length === 0;
  };

  const loadFirst = async(): Promise<void> => {
    const id = ++requestId;
    loading.value = true;
    error.value = false;
    moreError.value = false;
    errorMessage.value = null;
    loadingMore.value = false;
    items.value = [];
    nextPage.value = 0;
    totalElements.value = 0;
    exhausted.value = false;
    // Anything loadFirst can outrace has to be reset by loadFirst: bumping requestId is exactly
    // what stops the loser from cleaning up after itself.
    refreshing.value = false;
    try {
      await loadPage(id);
    } catch (err) {
      if (id === requestId) {
        error.value = true;
        errorMessage.value = extractError(err, $t);
      }
      onError?.(err, true);
    } finally {
      if (id === requestId) {
        loading.value = false;
      }
    }
  };

  /** First occurrence of each key, order preserved. */
  const dedupe = (rows: T[]): T[] => {
    const seen = new Set<string>();
    return rows.filter((row) => {
      if (seen.has(key(row))) {
        return false;
      }
      seen.add(key(row));
      return true;
    });
  };

  const refresh = async(): Promise<boolean> => {
    const pagesHeld = nextPage.value;
    // `refreshing` belongs in the guard, not only in the callers: two overlapping refreshes both
    // set the flag, and the first to settle clears it in its finally while the second is still
    // out. loadMore is let through in that gap, appends a page and advances nextPage, and then the
    // second refresh swaps in only the pages it asked for — dropping those rows with no error and
    // no way back to them.
    if (loading.value || loadingMore.value || refreshing.value || items.value.length === 0 || pagesHeld === 0) {
      return false;
    }
    // Bumped, not read: a loadMore already in flight must not write its page on top of the swap,
    // and a loadMore starting mid-refresh is turned away by the flag below. Without this the two
    // interleave and a page is dropped while the total still counts it.
    const id = ++requestId;
    refreshing.value = true;
    try {
      const results = await Promise.all(
        Array.from({length: pagesHeld}, async(_, page) => fetchPage(page)),
      );
      if (id !== requestId) {
        return false;
      }
      const merged = dedupe(results.flatMap((result) => result.items));
      const lastPage = results.at(-1);
      // Swapped only once every page is in, so a partial failure leaves the grid as it was.
      // eslint-disable-next-line require-atomic-updates -- guarded by the requestId bump above
      items.value = merged;
      totalElements.value = (lastPage?.totalElements ?? totalElements.value);
      return true;
    } catch (err) {
      // Deliberately quiet: the reader did not ask for this, and what is on screen is still usable.
      onError?.(err, false);
      return false;
    } finally {
      // Cleared unconditionally: this flag's owner must always release it, and the id guard above
      // is for discarding a stale *result*, not for deciding who cleans up. Only one refresh can
      // hold the flag now — the guard reads it synchronously, before any await — so the owner
      // clearing it is always the one that set it.
      // eslint-disable-next-line require-atomic-updates -- single owner, enforced by the guard above
      refreshing.value = false;
    }
  };

  const loadMore = async(): Promise<boolean> => {
    if (loadingMore.value || loading.value || refreshing.value
      || allLoaded.value || error.value || moreError.value) {
      return false;
    }
    const id = requestId;
    loadingMore.value = true;
    try {
      await loadPage(id);
    } catch (err) {
      if (id === requestId) {
        // Keeps the rows already on screen; the caller offers a retry beside them.
        // eslint-disable-next-line require-atomic-updates -- single-flight, guarded by loadingMore
        moreError.value = true;
        errorMessage.value = extractError(err, $t);
      }
      onError?.(err, false);
    } finally {
      if (id === requestId) {
        // eslint-disable-next-line require-atomic-updates -- single-flight, guarded by loadingMore
        loadingMore.value = false;
      }
    }

    return true;
  };

  const retryMore = async(): Promise<boolean> => {
    moreError.value = false;
    errorMessage.value = null;
    // Cleared too, or loadMore returns immediately on allLoaded and the retry does nothing.
    exhausted.value = false;
    return loadMore();
  };

  return {
    items,
    refresh,
    totalElements,
    loadedCount,
    allLoaded,
    stoppedShort,
    loading,
    loadingMore,
    error,
    moreError,
    errorMessage,
    loadFirst,
    loadMore,
    retryMore,
  };
}
